import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import LoginView from '@/views/LoginView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { public: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { public: true },
    },
    {
      path: '/pending',
      name: 'pending',
      component: () => import('@/views/PendingApprovalView.vue'),
      meta: { requiresAuth: true, allowPending: true },
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('@/views/ChangePasswordView.vue'),
      meta: { requiresAuth: true, allowTempPassword: true },
    },
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('@/views/OrdersView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/orders/:id',
      name: 'order-detail',
      component: () => import('@/views/OrderDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/reservations/:id',
      name: 'reservation-task-detail',
      component: () => import('@/views/ReservationTaskDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/reservations',
      name: 'my-reservations',
      component: () => import('@/views/MyReservationsView.vue'),
      meta: { requiresAuth: true, teacherOnly: true },
    },
    {
      path: '/reservations/:id/preorder',
      name: 'reservation-preorder',
      component: () => import('@/views/ReservationPreOrderView.vue'),
      meta: { requiresAuth: true, teacherOnly: true },
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/AdminDashboardView.vue'),
      meta: { requiresAuth: true, superAdminOnly: true },
    },
    {
      path: '/warehouse',
      name: 'warehouse',
      component: () => import('@/views/WarehouseDashboardView.vue'),
      meta: { requiresAuth: true, warehouseAdminOnly: true },
    },
    {
      path: '/materials',
      name: 'materials',
      component: () => import('@/views/MaterialsView.vue'),
      meta: { requiresAuth: true, teacherOnly: true },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/views/NotificationsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/notifications/broadcast',
      name: 'admin-notifications-broadcast',
      component: () => import('@/views/BroadcastView.vue'),
      meta: { requiresAuth: true, superAdminOnly: true },
    },
    {
      path: '/admin/reservations',
      name: 'admin-reservations',
      component: () => import('@/views/ReservationTaskManageView.vue'),
      meta: { requiresAuth: true, superAdminOnly: true },
    },
    {
      path: '/admin/preorders/revert-requests',
      name: 'admin-preorders-revert',
      component: () => import('@/views/PickupRevertRequestsView.vue'),
      meta: { requiresAuth: true, superAdminOnly: true },
    },
    {
      path: '/admin/notification-templates',
      name: 'admin-notification-templates',
      component: () => import('@/views/NotificationTemplatesView.vue'),
      meta: { requiresAuth: true, superAdminOnly: true },
    },
  ],
})

router.beforeEach((to) => {
  const authStore = useAuthStore()

  // 未登录访问需要登录的页面
  if (to.meta.requiresAuth && !authStore.isLoggedIn) {
    return '/login'
  }

  // 已登录但访问公开页面（登录/注册），跳到首页
  if (to.meta.public && authStore.isLoggedIn) {
    return '/'
  }

  // 老师未审批且不在 pending/登录/注册页，则强制去 pending
  if (
    authStore.isLoggedIn &&
    authStore.isTeacher &&
    !authStore.isApproved &&
    !to.meta.allowPending &&
    !to.meta.public
  ) {
    return '/pending'
  }

  if (to.meta.teacherOnly && !(authStore.isTeacher || authStore.isSuperAdmin)) {
    return '/'
  }
  if (to.meta.superAdminOnly && !authStore.isSuperAdmin) {
    return '/'
  }
  if (to.meta.warehouseAdminOnly && !(authStore.isWarehouseAdmin && authStore.warehouseId)) {
    return '/'
  }

  // 强改密拦截：必须先改密码
  if (
    authStore.isLoggedIn &&
    authStore.mustChangePassword &&
    to.name !== 'change-password' &&
    !to.meta.allowTempPassword
  ) {
    return '/change-password'
  }
})

export default router
